import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ReportButton from '../components/ReportButton';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, institutionsAPI, uploadAPI } from '../lib/api';
import { extUrl } from '../lib/url';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  User, MapPin, Briefcase, GraduationCap, Building, Globe,
  Linkedin, Phone, Mail, Edit2, Save, X, Video, Plus, Download, Loader2, Camera,
  Trash2, Instagram, Twitter, CalendarDays, ImageIcon, FileUp
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/api';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import usePageTitle from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import { Palette, Check, Moon, Sun } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPELHOUSE_INSTITUTIONS = ["Spelman College", "Morehouse College"];

const Profile = () => {
  usePageTitle('Profile');
  const { userId } = useParams();
  const { user, updateProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [importingLinkedin, setImportingLinkedin] = useState(false);
  const [spelhouseMode, setSpelhouseMode] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [workHistory, setWorkHistory] = useState([]);
  const [showAddWork, setShowAddWork] = useState(false);
  const [newWork, setNewWork] = useState({ title: '', company: '', start_date: '', end_date: '', description: '', current: false });
  const [extractedWork, setExtractedWork] = useState([]);
  const [extractingResume, setExtractingResume] = useState(false);
  const [showResumeReview, setShowResumeReview] = useState(false);
  const [selectedExtracted, setSelectedExtracted] = useState({});
  const [videoLinkMode, setVideoLinkMode] = useState(false);
  const [videoLinkUrl, setVideoLinkUrl] = useState('');
  const [savingVideoLink, setSavingVideoLink] = useState(false);
  const [recordingMode, setRecordingMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  const isOwnProfile = !userId || userId === user?.user_id;
  const isSpelhouse = SPELHOUSE_INSTITUTIONS.includes(profile?.institution);

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const [profileRes, instRes] = await Promise.all([
        isOwnProfile ? Promise.resolve({ data: user }) : usersAPI.getProfile(userId),
        institutionsAPI.list()
      ]);
      setProfile(profileRes.data);
      setEditData(profileRes.data);
      setInstitutions(instRes.data);
      setSpelhouseMode(profileRes.data?.spelhouse_mode || false);
      setWorkHistory(profileRes.data?.work_history || []);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateProfile(editData);
      setProfile(res);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleInterestAdd = (interest) => {
    if (interest && !editData.interests?.includes(interest)) {
      handleChange('interests', [...(editData.interests || []), interest]);
    }
  };

  const handleInterestRemove = (interest) => {
    handleChange('interests', editData.interests?.filter(i => i !== interest));
  };

  const handleLinkedInImport = async () => {
    if (!linkedinUrl.trim()) {
      toast.error('Please enter your LinkedIn URL');
      return;
    }

    setImportingLinkedin(true);
    try {
      const { default: api } = await import('../lib/api');
      const res = await api.post('/users/import-linkedin', { linkedin_url: linkedinUrl });
      setProfile(res.data.profile);
      setEditData(res.data.profile);
      toast.success('LinkedIn URL saved! Fill in your profile details below.');
      setLinkedinUrl('');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setImportingLinkedin(false);
    }
  };

  const handleSpelhouseToggle = async (enabled) => {
    try {
      const { default: api } = await import('../lib/api');
      await api.put('/users/spelhouse-mode', { enabled });
      setSpelhouseMode(enabled);
      toast.success(`SpelHouse mode ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    if (file.type && !validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, WebP, or HEIC image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const res = await uploadAPI.profilePhoto(file);
      const photoUrl = res.data.url;
      setProfile(prev => ({ ...prev, profile_photo: photoUrl }));
      setEditData(prev => ({ ...prev, profile_photo: photoUrl }));
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // On mobile, file.type can be empty — allow it through
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }
    setUploadingCover(true);
    try {
      const res = await uploadAPI.coverPhoto(file);
      const coverUrl = res.data.url;
      setProfile(prev => ({ ...prev, cover_photo: coverUrl }));
      toast.success('Cover photo updated!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAddWork = async () => {
    if (!newWork.title || !newWork.company) {
      toast.error('Title and company are required');
      return;
    }
    const updated = [...workHistory, { ...newWork, id: Date.now().toString() }];
    setWorkHistory(updated);
    try {
      await updateProfile({ work_history: updated });
      setProfile(prev => ({ ...prev, work_history: updated }));
      setShowAddWork(false);
      setNewWork({ title: '', company: '', start_date: '', end_date: '', description: '', current: false });
      toast.success('Work experience added!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleRemoveWork = async (id) => {
    const updated = workHistory.filter(w => w.id !== id);
    setWorkHistory(updated);
    try {
      await updateProfile({ work_history: updated });
      setProfile(prev => ({ ...prev, work_history: updated }));
      toast.success('Work experience removed');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    setExtractingResume(true);
    try {
      const res = await usersAPI.extractWorkHistory(file);
      const entries = res.data.work_history || [];
      if (entries.length === 0) {
        toast.error('No work experience found in the resume');
        return;
      }
      setExtractedWork(entries);
      const sel = {};
      entries.forEach((_, i) => { sel[i] = true; });
      setSelectedExtracted(sel);
      setShowResumeReview(true);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setExtractingResume(false);
      e.target.value = '';
    }
  };

  const handleConfirmResumeWork = async () => {
    const selected = extractedWork.filter((_, i) => selectedExtracted[i]);
    if (selected.length === 0) {
      toast.error('Select at least one entry');
      return;
    }
    const newEntries = selected.map(w => ({ ...w, id: Date.now().toString() + Math.random().toString(36).slice(2, 6) }));
    const updated = [...workHistory, ...newEntries];
    setWorkHistory(updated);
    try {
      await updateProfile({ work_history: updated });
      setProfile(prev => ({ ...prev, work_history: updated }));
      setShowResumeReview(false);
      setExtractedWork([]);
      toast.success(`Added ${selected.length} work experience${selected.length > 1 ? 's' : ''} from resume`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Cover & Profile Photo */}
        <div className="relative mb-6">
          <div className="h-40 sm:h-52 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-3xl overflow-hidden relative group">
            {profile?.cover_photo && (
              <img src={profile.cover_photo} alt="Cover" className="w-full h-full object-cover" />
            )}
            {isOwnProfile && (
              <label className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors cursor-pointer">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-2 bg-white/90 rounded-full px-4 py-2">
                  {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                  <span className="text-sm font-medium">{uploadingCover ? 'Uploading...' : 'Change Cover'}</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploadingCover} />
              </label>
            )}
          </div>
          <div className="absolute -bottom-12 left-6 sm:left-8">
            <div className="relative group">
              <Avatar className="w-28 h-28 sm:w-36 sm:h-36 border-4 border-white shadow-lg">
                <AvatarImage src={profile?.profile_photo} />
                <AvatarFallback className="bg-mint-fresh text-dark-slate text-4xl">
                  {profile?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              {isOwnProfile && (
                <label
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                  />
                </label>
              )}
            </div>
          </div>
          {isOwnProfile && (
            <Button
              onClick={() => editing ? handleSave() : setEditing(true)}
              disabled={saving}
              className="absolute top-4 right-4 rounded-full bg-white/90 text-dark-slate hover:bg-white"
              data-testid="edit-profile-btn"
            >
              {editing ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </>
              ) : (
                <>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </>
              )}
            </Button>
          )}
          {!isOwnProfile && profile?.user_id && (
            <div className="absolute top-4 right-4">
              <ReportButton contentType="user" contentId={profile.user_id} />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Name & Title */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={editData.name || ''}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="mt-1"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={editData.title || ''}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Software Engineer"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Company</Label>
                      <Input
                        value={editData.company || ''}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="Google"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={editData.bio || ''}
                      onChange={(e) => handleChange('bio', e.target.value)}
                      placeholder="Tell us about yourself..."
                      rows={4}
                      maxLength={500}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-gray mt-1">{(editData.bio || '').length}/500</p>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="font-heading font-extrabold text-3xl text-dark-slate mb-1">
                    {profile?.name}
                  </h1>
                  {profile?.title && (
                    <p className="text-lg text-slate-gray">
                      {profile.title}
                      {profile.company && ` at ${profile.company}`}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {profile?.graduation_year && (
                      <Badge variant="secondary" className="bg-mint-fresh/30 text-dark-slate">
                        Class of {profile.graduation_year}
                      </Badge>
                    )}
                    {profile?.user_type === 'service_provider' && (
                      <Badge className="bg-emerald-100 text-emerald-800">
                        Service Provider
                      </Badge>
                    )}
                  </div>
                  {profile?.bio && (
                    <p className="text-slate-gray mt-4">{profile.bio}</p>
                  )}
                </>
              )}
            </div>

            {/* Professional Experience */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">
                Professional Information
              </h2>
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Industry</Label>
                      <Input
                        value={editData.industry || ''}
                        onChange={(e) => handleChange('industry', e.target.value)}
                        placeholder="Technology"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Major</Label>
                      <Input
                        value={editData.major || ''}
                        onChange={(e) => handleChange('major', e.target.value)}
                        placeholder="Computer Science"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Institution</Label>
                      <Select value={editData.institution || ''} onValueChange={(v) => handleChange('institution', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select institution" />
                        </SelectTrigger>
                        <SelectContent>
                          {institutions.map(inst => (
                            <SelectItem key={inst.institution_id} value={inst.name}>
                              {inst.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Graduation Year</Label>
                      <Input
                        type="number"
                        value={editData.graduation_year || ''}
                        onChange={(e) => handleChange('graduation_year', parseInt(e.target.value))}
                        placeholder="2020"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {profile?.institution && (
                    <div className="flex items-center text-slate-gray">
                      <GraduationCap className="w-5 h-5 mr-3" />
                      <span>{profile.institution}</span>
                      {profile.graduation_year && <span className="ml-1">({profile.graduation_year})</span>}
                    </div>
                  )}
                  {profile?.major && (
                    <div className="flex items-center text-slate-gray">
                      <Briefcase className="w-5 h-5 mr-3" />
                      <span>{profile.major}</span>
                    </div>
                  )}
                  {profile?.industry && (
                    <div className="flex items-center text-slate-gray">
                      <Building className="w-5 h-5 mr-3" />
                      <span>{profile.industry}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Video Introduction */}
            {(profile?.video_intro || isOwnProfile) && (
              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                <h2 className="font-heading font-bold text-xl text-dark-slate mb-4 flex items-center">
                  <Video className="w-5 h-5 mr-2" />
                  Video Introduction
                </h2>
                {profile?.video_intro ? (
                  <div className="rounded-xl overflow-hidden bg-black aspect-video">
                    {profile.video_intro.match(/https?:\/\/(www\.)?youtube\.com\/watch\?v=/) ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${new URL(profile.video_intro).searchParams.get('v')}`}
                        className="w-full h-full"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        title="Video Introduction"
                      />
                    ) : profile.video_intro.match(/https?:\/\/youtu\.be\//) ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${profile.video_intro.split('youtu.be/')[1].split('?')[0]}`}
                        className="w-full h-full"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        title="Video Introduction"
                      />
                    ) : profile.video_intro.match(/https?:\/\/(www\.)?vimeo\.com\/\d+/) ? (
                      <iframe
                        src={`https://player.vimeo.com/video/${profile.video_intro.match(/vimeo\.com\/(\d+)/)[1]}`}
                        className="w-full h-full"
                        allowFullScreen
                        allow="autoplay; fullscreen; picture-in-picture"
                        title="Video Introduction"
                      />
                    ) : (
                      <video src={profile.video_intro} controls className="w-full h-full" />
                    )}
                  </div>
                ) : isOwnProfile ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                    <Video className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                    <p className="text-slate-gray text-sm mb-3">Add a 30-second video introduction</p>

                    {/* Recording UI */}
                    {recordingMode && (
                      <div className="mb-4">
                        <div className="rounded-xl overflow-hidden bg-black aspect-video mb-3 max-w-md mx-auto">
                          {recordedBlob ? (
                            <video
                              src={URL.createObjectURL(recordedBlob)}
                              controls
                              className="w-full h-full"
                            />
                          ) : (
                            <video
                              ref={(el) => { if (el && mediaStream) el.srcObject = mediaStream; }}
                              autoPlay
                              muted
                              playsInline
                              className="w-full h-full"
                            />
                          )}
                        </div>
                        <div className="flex items-center justify-center gap-2 mb-3">
                          {!recordedBlob && !isRecording && (
                            <Button
                              size="sm"
                              className="rounded-full bg-red-600 hover:bg-red-700"
                              onClick={() => {
                                if (!mediaStream) return;
                                const chunks = [];
                                let recorder;
                                try {
                                  recorder = new MediaRecorder(mediaStream, { mimeType: 'video/webm' });
                                } catch {
                                  recorder = new MediaRecorder(mediaStream);
                                }
                                recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
                                recorder.onstop = () => {
                                  const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
                                  setRecordedBlob(blob);
                                };
                                setMediaRecorder(recorder);
                                recorder.start();
                                setIsRecording(true);
                              }}
                            >
                              <Camera className="w-4 h-4 mr-1" /> Start Recording
                            </Button>
                          )}
                          {isRecording && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="rounded-full"
                              onClick={() => {
                                if (mediaRecorder && mediaRecorder.state !== 'inactive') {
                                  mediaRecorder.stop();
                                }
                                setIsRecording(false);
                              }}
                            >
                              <X className="w-4 h-4 mr-1" /> Stop Recording
                            </Button>
                          )}
                          {recordedBlob && (
                            <>
                              <Button
                                size="sm"
                                className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                                onClick={async () => {
                                  try {
                                    toast.info('Uploading recording...');
                                    const file = new File([recordedBlob], 'video-intro.webm', { type: recordedBlob.type || 'video/webm' });
                                    const res = await uploadAPI.videoIntro(file);
                                    setProfile(prev => ({ ...prev, video_intro: res.data.url }));
                                    toast.success('Video intro uploaded!');
                                    if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
                                    setRecordingMode(false);
                                    setRecordedBlob(null);
                                    setMediaStream(null);
                                    setMediaRecorder(null);
                                  } catch (error) {
                                    toast.error(formatApiError(error));
                                  }
                                }}
                              >
                                <Save className="w-4 h-4 mr-1" /> Save Recording
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => setRecordedBlob(null)}
                              >
                                Retake
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() => {
                              if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
                              if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
                              setRecordingMode(false);
                              setIsRecording(false);
                              setRecordedBlob(null);
                              setMediaStream(null);
                              setMediaRecorder(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Paste Link UI */}
                    {videoLinkMode && (
                      <div className="mb-4 max-w-md mx-auto">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Paste YouTube, Vimeo, or video URL..."
                            value={videoLinkUrl}
                            onChange={(e) => setVideoLinkUrl(e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                            disabled={!videoLinkUrl.trim() || savingVideoLink}
                            onClick={async () => {
                              setSavingVideoLink(true);
                              try {
                                await usersAPI.updateProfile({ video_intro: videoLinkUrl.trim() });
                                setProfile(prev => ({ ...prev, video_intro: videoLinkUrl.trim() }));
                                toast.success('Video link saved!');
                                setVideoLinkUrl('');
                                setVideoLinkMode(false);
                              } catch (error) {
                                toast.error(formatApiError(error));
                              } finally {
                                setSavingVideoLink(false);
                              }
                            }}
                          >
                            {savingVideoLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </Button>
                        </div>
                        <button
                          className="text-xs text-slate-gray mt-2 hover:underline"
                          onClick={() => { setVideoLinkMode(false); setVideoLinkUrl(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {!recordingMode && !videoLinkMode && (
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <label className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-emerald-700 transition-colors">
                          <Video className="w-4 h-4 mr-2" />
                          Upload Video
                          <input
                            type="file"
                            accept="video/mp4,video/mov,video/webm,video/quicktime,video/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 50 * 1024 * 1024) {
                                toast.error('Video too large. Max 50MB');
                                return;
                              }
                              try {
                                toast.info('Uploading video...');
                                const res = await uploadAPI.videoIntro(file);
                                setProfile(prev => ({ ...prev, video_intro: res.data.url }));
                                toast.success('Video intro uploaded!');
                              } catch (error) {
                                toast.error(formatApiError(error));
                              }
                            }}
                          />
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => setVideoLinkMode(true)}
                        >
                          <Globe className="w-4 h-4 mr-2" />
                          Paste Link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={async () => {
                            try {
                              const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                              setMediaStream(stream);
                              setRecordingMode(true);
                            } catch (err) {
                              toast.error('Could not access camera. Please check permissions.');
                            }
                          }}
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Record Video
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-gray mt-2">MP4, MOV, or WebM • Max 50MB</p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Work History */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl text-dark-slate">Work Experience</h2>
                {isOwnProfile && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('resume-upload').click()} className="rounded-full" disabled={extractingResume}>
                      {extractingResume ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileUp className="w-4 h-4 mr-1" />}
                      {extractingResume ? 'Extracting...' : 'From Resume'}
                    </Button>
                    <input id="resume-upload" type="file" accept=".pdf" className="hidden" onChange={handleResumeUpload} />
                    <Button variant="outline" size="sm" onClick={() => setShowAddWork(true)} className="rounded-full">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                )}
              </div>
              {workHistory.length === 0 ? (
                <p className="text-slate-gray text-sm">
                  {isOwnProfile ? 'Add your work experience to showcase your career journey.' : 'No work experience listed.'}
                </p>
              ) : (
                <div className="space-y-4">
                  {workHistory.map((work) => (
                    <div key={work.id} className="relative pl-6 border-l-2 border-slate-200 pb-4 last:pb-0">
                      <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-dark-slate border-2 border-white" />
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-dark-slate">{work.title}</h3>
                          <p className="text-slate-gray text-sm">{work.company}</p>
                          <p className="text-xs text-slate-gray mt-1">
                            {work.start_date || 'Start'} — {work.current ? 'Present' : (work.end_date || 'End')}
                          </p>
                          {work.description && (
                            <p className="text-sm text-slate-gray mt-2">{work.description}</p>
                          )}
                        </div>
                        {isOwnProfile && (
                          <button onClick={() => handleRemoveWork(work.id)} className="text-slate-gray hover:text-red-500 ml-2">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Work Dialog */}
            <Dialog open={showAddWork} onOpenChange={setShowAddWork}>
              <DialogContent className="rounded-2xl">
                <DialogHeader>
                  <DialogTitle>Add Work Experience</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Job Title *</Label>
                    <Input value={newWork.title} onChange={(e) => setNewWork(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. Software Engineer" className="mt-1" />
                  </div>
                  <div>
                    <Label>Company *</Label>
                    <Input value={newWork.company} onChange={(e) => setNewWork(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="e.g. Google" className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Start Date</Label>
                      <Input type="month" value={newWork.start_date}
                        onChange={(e) => setNewWork(prev => ({ ...prev, start_date: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input type="month" value={newWork.end_date} disabled={newWork.current}
                        onChange={(e) => setNewWork(prev => ({ ...prev, end_date: e.target.value }))} className="mt-1" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch checked={newWork.current}
                      onCheckedChange={(v) => setNewWork(prev => ({ ...prev, current: v, end_date: v ? '' : prev.end_date }))} />
                    <Label>I currently work here</Label>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={newWork.description}
                      onChange={(e) => setNewWork(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your role..." rows={3} className="mt-1" />
                  </div>
                  <Button onClick={handleAddWork} className="w-full rounded-full bg-dark-slate text-white hover:bg-slate-800">
                    Add Experience
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Resume Work History Review Dialog */}
            <Dialog open={showResumeReview} onOpenChange={setShowResumeReview}>
              <DialogContent className="rounded-2xl max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Work Experience Found in Resume</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-gray mb-4">Select the entries you'd like to add to your profile:</p>
                <div className="space-y-3">
                  {extractedWork.map((work, i) => (
                    <div key={i} className={`p-3 rounded-xl border ${selectedExtracted[i] ? 'border-mint-fresh bg-mint-fresh/5' : 'border-slate-200'} cursor-pointer transition-colors`}
                      onClick={() => setSelectedExtracted(prev => ({ ...prev, [i]: !prev[i] }))}>
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${selectedExtracted[i] ? 'bg-mint-fresh border-mint-fresh' : 'border-slate-300'}`}>
                          {selectedExtracted[i] && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-dark-slate text-sm">{work.title}</h4>
                          <p className="text-slate-gray text-sm">{work.company}</p>
                          <p className="text-xs text-slate-gray mt-1">
                            {work.start_date || 'Start'} — {work.current ? 'Present' : (work.end_date || 'End')}
                          </p>
                          {work.description && <p className="text-xs text-slate-gray mt-1 line-clamp-2">{work.description}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button onClick={handleConfirmResumeWork} className="w-full rounded-full bg-dark-slate text-white hover:bg-slate-800 mt-4">
                  Add {Object.values(selectedExtracted).filter(Boolean).length} Selected to Profile
                </Button>
              </DialogContent>
            </Dialog>

            {/* Interests */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">
                Interests
              </h2>
              {editing ? (
                <div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(editData.interests || []).map((interest, i) => (
                      <Badge key={i} variant="secondary" className="bg-slate-100 pl-3 pr-1 py-1">
                        {interest}
                        <button 
                          onClick={() => handleInterestRemove(interest)}
                          className="ml-2 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="new-interest"
                      placeholder="Add interest..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleInterestAdd(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById('new-interest');
                        handleInterestAdd(input.value);
                        input.value = '';
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {profile?.interests?.length > 0 ? (
                    profile.interests.map((interest, i) => (
                      <Badge key={i} variant="secondary" className="bg-mint-fresh/30 text-dark-slate">
                        {interest}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-slate-gray">No interests added yet</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Completion */}
            {isOwnProfile && (
              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                <h3 className="font-semibold text-dark-slate mb-3">Profile Completion</h3>
                <Progress value={profile?.profile_completion || 0} className="h-2 mb-2" />
                <p className="text-sm text-slate-gray">{profile?.profile_completion}% complete</p>
              </div>
            )}

            {/* Details */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <h3 className="font-semibold text-dark-slate mb-4">Details</h3>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Hometown</Label>
                    <Input
                      value={editData.hometown || ''}
                      onChange={(e) => handleChange('hometown', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Current Location</Label>
                    <Input
                      value={editData.current_location || ''}
                      onChange={(e) => handleChange('current_location', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Greek Life</Label>
                    <Input
                      value={editData.greek_life || ''}
                      onChange={(e) => handleChange('greek_life', e.target.value)}
                      placeholder="Alpha Kappa Alpha"
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  {profile?.current_location && (
                    <div className="flex items-center text-slate-gray">
                      <MapPin className="w-4 h-4 mr-2" />
                      {profile.current_location}
                    </div>
                  )}
                  {profile?.hometown && (
                    <div className="flex items-center text-slate-gray">
                      <User className="w-4 h-4 mr-2" />
                      From {profile.hometown}
                    </div>
                  )}
                  {profile?.greek_life && (
                    <div className="flex items-center text-slate-gray">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      {profile.greek_life}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Contact & Links */}
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
              <h3 className="font-semibold text-dark-slate mb-4">Links</h3>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label>LinkedIn URL</Label>
                    <Input
                      value={editData.linkedin_url || ''}
                      onChange={(e) => handleChange('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={editData.website || ''}
                      onChange={(e) => handleChange('website', e.target.value)}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={editData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Twitter / X</Label>
                    <Input
                      value={editData.twitter_url || ''}
                      onChange={(e) => handleChange('twitter_url', e.target.value)}
                      placeholder="https://x.com/yourhandle"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      value={editData.instagram_url || ''}
                      onChange={(e) => handleChange('instagram_url', e.target.value)}
                      placeholder="https://instagram.com/yourhandle"
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  {profile?.linkedin_url && (
                    <a href={extUrl(profile.linkedin_url)} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                      <Linkedin className="w-4 h-4 mr-2" />
                      LinkedIn
                    </a>
                  )}
                  {profile?.website && (
                    <a href={extUrl(profile.website)} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                    </a>
                  )}
                  {profile?.twitter_url && (
                    <a href={extUrl(profile.twitter_url)} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                      <Twitter className="w-4 h-4 mr-2" />
                      Twitter / X
                    </a>
                  )}
                  {profile?.instagram_url && (
                    <a href={extUrl(profile.instagram_url)} target="_blank" rel="noopener noreferrer" className="flex items-center text-pink-600 hover:underline">
                      <Instagram className="w-4 h-4 mr-2" />
                      Instagram
                    </a>
                  )}
                  {profile?.phone && (
                    <div className="flex items-center text-slate-gray">
                      <Phone className="w-4 h-4 mr-2" />
                      {profile.phone}
                    </div>
                  )}
                  <div className="text-slate-gray">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {profile?.email}
                    </div>
                    {isOwnProfile && (
                      <Link to="/settings" className="text-xs text-dark-slate hover:underline ml-6">Change</Link>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* LinkedIn Import */}
            {isOwnProfile && !editing && (
              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
                <h3 className="font-semibold text-dark-slate mb-3 flex items-center">
                  <Linkedin className="w-5 h-5 mr-2 text-blue-600" />
                  Import from LinkedIn
                </h3>
                <p className="text-sm text-slate-gray mb-4">
                  Auto-fill your profile from LinkedIn
                </p>
                <div className="space-y-3">
                  <Input
                    placeholder="https://linkedin.com/in/yourprofile"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    data-testid="linkedin-url-input"
                  />
                  <Button
                    onClick={handleLinkedInImport}
                    disabled={importingLinkedin}
                    className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="linkedin-import-btn"
                  >
                    {importingLinkedin ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Import Profile
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Theme Picker */}
            {isOwnProfile && <ThemePicker />}

            {/* SpelHouse Mode Toggle */}
            {isOwnProfile && isSpelhouse && (
              <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-3xl p-6 border border-rose-100 shadow-ambient">
                <h3 className="font-semibold text-dark-slate mb-2 flex items-center">
                  <span className="text-xl mr-2">🎓</span>
                  SpelHouse Mode
                </h3>
                <p className="text-sm text-slate-gray mb-4">
                  See only Spelman & Morehouse alumni in your feed and connections
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-dark-slate font-medium">
                    {spelhouseMode ? 'Enabled' : 'Disabled'}
                  </span>
                  <Switch
                    checked={spelhouseMode}
                    onCheckedChange={handleSpelhouseToggle}
                    data-testid="spelhouse-toggle"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

const ThemePicker = () => {
  const { themeId, setTheme, themes, isDark } = useTheme();

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient" style={{ backgroundColor: 'hsl(var(--card))' }}>
      <h3 className="font-semibold text-dark-slate mb-1 flex items-center">
        <Palette className="w-5 h-5 mr-2 text-slate-gray" />
        Appearance
      </h3>
      <p className="text-sm text-slate-gray mb-4">
        Choose a color theme for your GradLink experience
      </p>

      {/* Quick dark/light toggle */}
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl border border-slate-100" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2">
          {isDark ? <Moon className="w-4 h-4 text-slate-gray" /> : <Sun className="w-4 h-4 text-amber-500" />}
          <span className="text-sm font-medium text-dark-slate">
            {isDark ? 'Dark Mode' : 'Light Mode'}
          </span>
        </div>
        <Switch
          checked={isDark}
          onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'default')}
        />
      </div>

      {/* Theme grid */}
      <div className="grid grid-cols-4 gap-2">
        {themes.map((t) => (
          <button
            key={t.id}
            onClick={() => setTheme(t.id)}
            className={`relative flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${
              themeId === t.id
                ? 'border-blue-500 ring-2 ring-blue-200 scale-[1.02]'
                : 'border-transparent hover:border-slate-200'
            }`}
            title={t.name}
          >
            {/* Color preview dots */}
            <div className="flex gap-0.5">
              {t.preview.map((color, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full border border-slate-200/50"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <span className="text-[10px] text-slate-gray leading-tight text-center truncate w-full">
              {t.name}
            </span>
            {themeId === t.id && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <Check className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Profile;
